import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { FIREBOLT_TERMS_AND_CONDITIONS_URL } from "utils/constants";

import { AiSurfaceState, useAiAccessPolicy } from "hooks/useAiAccessPolicy";
import { useOrganizationSettings } from "services/organization/useOrganizationSettings";

import CopilotIcon from "assets/icons/Copilot.svg?react";

import { useCopilot } from "pages/DevelopWorkspace/Editor/Copilot/CopilotProvider";
import { DocumentStatus } from "pages/DevelopWorkspace/Editor/Document/Document";
import RunButton from "pages/DevelopWorkspace/Editor/Document/DocumentContent/RunButton/RunButton";
import FlagsMenu from "pages/DevelopWorkspace/Editor/Document/FlagsMenu";
import useActiveEditorView from "pages/DevelopWorkspace/contexts/ActiveEditorViewContext/hooks/useActiveEditorView";
import { useDocuments } from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useDocuments";
import {
  CancellationStatus,
  ExecutionType,
  WorkspaceDocument,
} from "pages/DevelopWorkspace/workspace.types";

import { ConfirmationModal } from "components/ConfirmationModal/ConfirmationModal";

import styles from "./DocumentActionsPanel.module.scss";

interface DocumentActionsPanelProps {
  document: WorkspaceDocument;
  documentStatus: DocumentStatus;
  executionType: ExecutionType;
  onExecutionTypeChange: (type: ExecutionType) => void;
}

const DocumentActionsPanel = (props: DocumentActionsPanelProps) => {
  const { document, documentStatus, executionType, onExecutionTypeChange } =
    props;

  const { copilotState, openCopilot } = useCopilot();
  const aiPolicy = useAiAccessPolicy();
  const { setSetting, isSettingPending } = useOrganizationSettings();
  const [showEnableModal, setShowEnableModal] = useState(false);
  const {
    actions: {
      startDocumentExecution,
      changeDocumentContext,
      cancelDocumentExecution,
    },
    shouldGenerateSampleScript,
  } = useDocuments();

  const { activeEditorView } = useActiveEditorView();

  const { t } = useTranslation();

  const isChatbotOpened = copilotState.open;

  const handleChangeExecutionType = (type: ExecutionType) => {
    onExecutionTypeChange(type);
  };

  const cancelDocument = () => {
    if (
      document.execution?.cancellationStatus === CancellationStatus.Initiated ||
      document.execution?.cancellationStatus === CancellationStatus.Pending
    ) {
      return;
    }

    cancelDocumentExecution(document.id);
  };

  const getRunPayload = () => {
    if (!activeEditorView) {
      return null;
    }

    const docText = activeEditorView.state.doc.toString();
    if (docText === "") {
      return null;
    }

    const range = activeEditorView.state.selection.ranges[0];
    const isSelection = range.from !== range.to;
    const selectionOffset = isSelection ? range.from : 0;
    const lineNumber = isSelection
      ? activeEditorView.state.doc.lineAt(range.from).number
      : 1;
    const textToRun = isSelection
      ? activeEditorView.state.doc.sliceString(range.from, range.to)
      : docText;

    return { textToRun, selectionOffset, lineNumber } as const;
  };

  const runDocument = () => {
    if (documentStatus.isRunning) {
      cancelDocument();
    }

    if (!activeEditorView) {
      return;
    }

    const payload = getRunPayload();
    if (!payload) return;
    const { textToRun, selectionOffset, lineNumber } = payload;
    startDocumentExecution({
      documentId: document.id,
      selectedContent: textToRun,
      selectionOffset,
      lineNumber,
      executionType,
    });
  };

  return (
    <div className={styles.documentActionsPanel}>
      {(Object.keys(document.context.settings).length > 0 ||
        Object.keys(document.context.machineGeneratedSettings).length > 0) && (
        <div className={styles.control}>
          <FlagsMenu
            settings={document.context.settings}
            isDocumentRunning={documentStatus.isRunning}
            onRemoveAll={() => {
              changeDocumentContext(document.id, {
                settings: {},
                machineGeneratedSettings: {},
              });
            }}
            onRemoveSetting={flag => {
              const newSettings = { ...document.context.settings };
              delete newSettings[flag];

              changeDocumentContext(document.id, {
                settings: {
                  ...newSettings,
                },
              });
            }}
          />
        </div>
      )}
      {aiPolicy.chat === AiSurfaceState.Enabled && !isChatbotOpened && (
        <div
          className={styles.copilot}
          onClick={openCopilot}
        >
          <CopilotIcon />
          <span className={styles.copilot__text}>{t("copilot.ask")}</span>
        </div>
      )}
      {aiPolicy.chat === AiSurfaceState.AdminCta && (
        <div
          className={styles.copilot}
          onClick={() => setShowEnableModal(true)}
        >
          <CopilotIcon />
          <span className={styles.copilot__text}>{t("copilot.ask")}</span>
        </div>
      )}

      {showEnableModal && (
        <ConfirmationModal
          title={t("ai.enable.confirm.title")}
          isLoading={isSettingPending}
          message={
            <Trans
              i18nKey="ai.enable.confirm.message"
              components={{
                a: (
                  // eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/control-has-associated-label
                  <a
                    href={FIREBOLT_TERMS_AND_CONDITIONS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          }
          confirmButtonOptions={{
            text: t("ai.enable.confirm.accept"),
          }}
          onClose={() => setShowEnableModal(false)}
          onConfirm={async () => {
            await setSetting({
              key: "firebolt_agent_enabled",
              value: true,
            });
            setShowEnableModal(false);
            openCopilot();
          }}
          objectType={undefined}
          objectName={undefined}
        />
      )}

      <RunButton
        executionType={executionType}
        handleChangeExecutionType={handleChangeExecutionType}
        isRunning={documentStatus.isRunning}
        isUnknown={documentStatus.isUnknown}
        isRestoring={documentStatus.isRestoring}
        isCanceling={documentStatus.isCanceling}
        isFirstTemplateScript={shouldGenerateSampleScript}
        onClick={() => {
          if (documentStatus.isUnknown || documentStatus.isCanceling) {
            return;
          }
          if (documentStatus.isRunning) {
            cancelDocument();
            return;
          }

          runDocument();
        }}
      />
    </div>
  );
};

export default DocumentActionsPanel;
